import { GET, POST, PUT } from '../dataSaver';
import CAPManager from '../../images/CAPManagerSmallLogo.png';
import MDManager from '../../images/MDManager.png';
import PNPManager from '../../images/PNPManager.png';
import EDUSmart from '../../images/EDUSmart.png';
import SureShield from '../../images/SureShieldGreen.png';
import TimeSmartLogo from '../../images/timeSmartAILogo.png';

export const PORTAL_TILES_URL = 'user-management-service/portal/tiles';

/** Build URL with optional query params (skips empty values). */
export const withQueryParams = (url, params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    const normalized = String(value).trim();
    if (!normalized) return;
    search.set(key, normalized);
  });
  const qs = search.toString();
  return qs ? `${url}?${qs}` : url;
};

/** UI-only config keyed by API tileKey (accent, kind, product workspace, fallback logo). */
export const PORTAL_TILE_UI = {
  CAPSMART: {
    kind: 'workspace',
    workspace: 'CAP_MANAGER',
    accent: '#06617A',
    fallbackImage: CAPManager,
    titleParts: { lead: 'CAP', accent: 'Smart' },
  },
  MD_MANAGER: {
    kind: 'workspace',
    workspace: 'MD_MANAGER',
    accent: '#1F7A5C',
    fallbackImage: MDManager,
    titleParts: { lead: 'MD', accent: 'Manager' },
  },
  PNP_MANAGER: {
    kind: 'workspace',
    workspace: 'PNP_MANAGER',
    accent: '#8A5A1A',
    fallbackImage: PNPManager,
    titleParts: { lead: 'P&P', accent: 'Manager', pnp: true },
  },
  EDUSMART: {
    kind: 'workspace',
    workspace: 'LMS_MANAGER',
    accent: '#2F6B9A',
    fallbackImage: EDUSmart,
    titleParts: { lead: 'EDU', accent: 'Smart' },
  },
  CONTRACTSMART: {
    kind: 'workspace',
    workspace: 'CONTRACT_MANAGER',
    accent: '#3D4F6F',
    fallbackImage: CAPManager,
    titleParts: { lead: 'Contract', accent: 'Smart' },
  },
  TIMESMART: {
    kind: 'workspace',
    workspace: 'TIMESMART',
    accent: '#5A6B3D',
    fallbackImage: TimeSmartLogo,
    titleParts: { lead: 'Time', accent: 'Smart' },
  },
  INTEGRITY_SHIELD: {
    kind: 'workspace',
    workspace: 'INTEGRITY_SHIELD',
    accent: '#7A3E4B',
    fallbackImage: SureShield,
    titleParts: { lead: 'Integrity ', accent: 'Shield' },
  },
  PROVIDER_PORTAL: {
    kind: 'workspace',
    workspace: 'PROVIDER_PORTAL',
    accent: '#06617A',
    fallbackImage: CAPManager,
    titleParts: { lead: 'Provider ', accent: 'Portal' },
  },
};

const resolveLogoUrl = (tile) =>
  tile?.logoUrl ||
  tile?.logoFileUrl ||
  tile?.logo?.fileURL ||
  tile?.logo?.file?.fileURL ||
  null;

export const normalizeTilesList = (data) => {
  const tiles = Array.isArray(data) ? data : data?.tiles || [];
  return [...tiles];
};

export const normalizeTilesResponse = (data) =>
  normalizeTilesList(data).sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
  );

export const fetchPortalTiles = async ({ shareId } = {}) => {
  const { data } = await GET(withQueryParams(PORTAL_TILES_URL, { shareId }));
  // Shared Demo Studio catalogs must keep the share-specific API order.
  // Do not re-sort by global displayOrder when shareId is present.
  if (shareId) {
    return normalizeTilesList(data);
  }
  return normalizeTilesResponse(data);
};

/** Single portal tile (includes demoStudio page/hero copy fields). */
export const fetchPortalTile = async (tileKey, { shareId } = {}) => {
  const { data } = await GET(
    withQueryParams(`${PORTAL_TILES_URL}/${encodeURIComponent(tileKey)}`, {
      shareId,
    })
  );
  return data;
};

export const buildPutPayload = (tiles) => ({
  tiles: tiles.map((tile, index) => ({
    tileKey: tile.tileKey,
    subtitle: tile.subtitle ?? '',
    title: tile.title ?? '',
    description: tile.description ?? '',
    buttonText: tile.buttonText ?? '',
    // Application tiles use inner demo studio; URL is not used at this level.
    navigationUrl: '',
    demoStudioPageTitle: tile.demoStudioPageTitle ?? '',
    demoStudioPageHelpText: tile.demoStudioPageHelpText ?? '',
    demoStudioDescription: tile.demoStudioDescription ?? '',
    displayOrder: index + 1,
    active: tile.active !== false,
  })),
});

/**
 * Lean reorder payload for Sales Rep / Prospect Demo Studios.
 * Backend scopes by shareId and must not receive the global tile catalog.
 */
export const buildSharePortalTilesPutPayload = (tiles) => ({
  tiles: (tiles || [])
    .filter((tile) => tile?.tileKey)
    .map((tile, index) => ({
      tileKey: tile.tileKey,
      title: tile.title ?? '',
      displayOrder: index + 1,
    })),
});

export const savePortalTiles = async (tiles, { shareId } = {}) => {
  const payload = shareId
    ? buildSharePortalTilesPutPayload(tiles)
    : buildPutPayload(tiles);
  return PUT(withQueryParams(PORTAL_TILES_URL, { shareId }), payload);
};

export const uploadPortalTileLogo = async (tileKey, file) => {
  const formData = new FormData();
  formData.append('logoFile', file);
  return POST(`${PORTAL_TILES_URL}/${encodeURIComponent(tileKey)}/logo`, formData);
};

/** Merge API tile with static UI config for /demo rendering. */
export const mergeTileForDemo = (apiTile, index) => {
  const ui = PORTAL_TILE_UI[apiTile.tileKey] || {};
  const logoUrl = resolveLogoUrl(apiTile);
  const navigationUrl = (apiTile.navigationUrl || '').trim();
  let kind = ui.kind || 'workspace';
  if (navigationUrl) {
    kind = 'external';
  }

  return {
    ...apiTile,
    id: apiTile.tileKey,
    kind,
    workspace: ui.workspace,
    accent: ui.accent || '#06617A',
    titleParts: ui.titleParts || null,
    image: logoUrl || ui.fallbackImage || null,
    title: apiTile.title,
    subtitle: apiTile.subtitle,
    description: apiTile.description,
    cta: apiTile.buttonText,
    url: navigationUrl,
    badge: String(index + 1).padStart(2, '0'),
  };
};

export const reorderTiles = (tiles, fromIndex, toIndex) => {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= tiles.length ||
    toIndex >= tiles.length
  ) {
    return tiles;
  }
  const next = [...tiles];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next.map((tile, index) => ({
    ...tile,
    displayOrder: index + 1,
  }));
};

/** Prefer accent-styled titleParts when API title still matches the UI brand label. */
export const shouldUseTitleParts = (title, titleParts) => {
  if (!titleParts) return false;
  const normalizedTitle = String(title || '').replace(/\s+/g, '').toLowerCase();
  const normalizedParts = `${titleParts.lead || ''}${titleParts.accent || ''}`
    .replace(/\s+/g, '')
    .toLowerCase();
  return Boolean(normalizedTitle) && normalizedTitle === normalizedParts;
};

/**
 * Reorder only active tiles inside the full list, preserving inactive slots.
 * Used by /demo so order PUT does not drop inactive tiles.
 */
export const applyActiveTileOrder = (allTiles, orderedActiveKeys) => {
  const byKey = Object.fromEntries(
    allTiles.map((tile) => [tile.tileKey, tile])
  );
  let activeCursor = 0;
  const merged = allTiles.map((tile) => {
    if (tile.active === false) return { ...tile };
    const key = orderedActiveKeys[activeCursor++];
    return { ...(byKey[key] || tile) };
  });
  return merged.map((tile, index) => ({
    ...tile,
    displayOrder: index + 1,
  }));
};
